import React, { useEffect, useState, useCallback } from 'react';
import api from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';
import PageHeader from '../../../components/common/PageHeader';
import ModuleHeaderStrip from '../../../components/common/ModuleHeaderStrip';
import AlertToast from '../../../components/common/AlertToast';
import Pagination from '../../../components/common/Pagination';

const DEFAULT_CLASSES = [
  'Pre-Primary (Nursery)', 'Pre-Primary (LKG)', 'Pre-Primary (UKG)',
  'Class 1', 'Class 2', 'Class 3', 'Class 4', 'Class 5',
  'Class 6', 'Class 7', 'Class 8', 'Class 9', 'Class 10',
  'Class 11 (Science)', 'Class 11 (Commerce)', 'Class 11 (Arts)',
  'Class 12 (Science)', 'Class 12 (Commerce)', 'Class 12 (Arts)',
  'Diploma', 'Certificate Course',
];

const STEPS = [
  'Applied', 'Documents Verified', 'Test/Interview Scheduled',
  'Test/Interview Done', 'Offer Released', 'Enrolled', 'Rejected',
];
const STEP_COLORS = {
  'Applied': '#6366f1',
  'Documents Verified': '#0ea5e9',
  'Test/Interview Scheduled': '#f59e0b',
  'Test/Interview Done': '#8b5cf6',
  'Offer Released': '#22c55e',
  'Enrolled': '#16a34a',
  'Rejected': '#ef4444',
};
const SOURCES = ['Website', 'Walk-in', 'Phone Call', 'Social Media', 'Referral', 'Agent', 'Other'];
const SECTIONS = ['A', 'B', 'C', 'D', 'E'];

const blank = {
  applicant_name: '', email: '', phone: '', dob: '',
  gender: '', address: '', programme: '', section: '', source: '',
  father_name: '', mother_name: '', parent_phone: '',
  has_previous_school: false, previous_school: '', percentage: '',
  documents_submitted: false, test_date: '', test_notes: '',
  status: 'Applied', remarks: '',
};

export default function AdmissionManagement({ moduleKey = 'admission_management' }) {
  const { user } = useAuth();
  const isAdmin = user?.roleKey === 'admin' || user?.roleKey === 'manager';

  const [records,       setRecords]       = useState([]);
  const [total,         setTotal]         = useState(0);
  const [page,          setPage]          = useState(1);
  const [loading,       setLoading]       = useState(true);
  const [showForm,      setShowForm]      = useState(false);
  const [editing,       setEditing]       = useState(null);
  const [filterStatus,  setFilterStatus]  = useState('');
  const [search,        setSearch]        = useState('');
  const [saving,        setSaving]        = useState(false);
  const [form,          setForm]          = useState(blank);
  const [toast,         setToast]         = useState({ show: false, message: '', type: 'success' });
  const [classOptions,  setClassOptions]  = useState(DEFAULT_CLASSES);
  const [sectionOptions, setSectionOptions] = useState(['A', 'B', 'C', 'D', 'E']);
  const [otherSource,   setOtherSource]   = useState('');
  const [viewRecord,    setViewRecord]    = useState(null);

  // Load class options from school config
  useEffect(() => {
    api.get('/records/school_config', { params: { limit: 1 } })
      .then(r => {
        const rec = r.data?.records?.[0]?.recordData || r.data?.record?.recordData;
        if (rec) {
          if (rec.classes?.length) {
            setClassOptions(rec.classes);
          } else if (rec.total_classes) {
            const generated = [];
            for (let i = 1; i <= Number(rec.total_classes); i++) generated.push(`Class ${i}`);
            if (generated.length > 0) setClassOptions(generated);
          }
          if (rec.sections?.length) setSectionOptions(rec.sections);
        }
      }).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    const params = { page, limit: 20, search };
    if (filterStatus) params['recordData.status'] = filterStatus;
    const r = await api.get(`/records/${moduleKey}`, { params })
      .catch(() => ({ data: { records: [], total: 0 } }));
    setRecords(r.data.records || []);
    setTotal(r.data.total || 0);
    setLoading(false);
  }, [moduleKey, page, search, filterStatus]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setForm(blank); setEditing(null); setOtherSource(''); setShowForm(true); setViewRecord(null);
  };
  const openEdit = (r) => {
    const d = r.recordData || r;
    setForm({ ...blank, ...d });
    setOtherSource(!SOURCES.includes(d.source) && d.source ? d.source : '');
    setEditing(r); setShowForm(true); setViewRecord(null);
  };

  const handleSave = async (e) => {
    e.preventDefault(); setSaving(true);
    const payload = {
      ...form,
      source: form.source === 'Other' ? otherSource : form.source,
    };
    try {
      if (editing?._id) await api.put(`/records/${moduleKey}/${editing._id}`, payload);
      else              await api.post(`/records/${moduleKey}`, payload);
      setToast({ show: true, message: `Application ${editing ? 'updated' : 'submitted'}!`, type: 'success' });
      setShowForm(false); setEditing(null); load();
    } catch (err) {
      setToast({ show: true, message: err.response?.data?.message || 'Error saving', type: 'danger' });
    } finally { setSaving(false); }
  };

  const moveStep = async (record, step) => {
    try {
      await api.put(`/records/${moduleKey}/${record._id}`, { ...record.recordData, status: step });
      setToast({ show: true, message: `Status → ${step}`, type: 'success' }); load();
    } catch { setToast({ show: true, message: 'Failed to update status', type: 'danger' }); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this application?')) return;
    await api.delete(`/records/${moduleKey}/${id}`).catch(() => {});
    load();
  };

  const sf = k => v => setForm(f => ({ ...f, [k]: v }));
  const sfE = k => e => sf(k)(e.target.value);

  const statsByStatus = STEPS.reduce((acc, s) => {
    acc[s] = records.filter(r => r.recordData?.status === s).length;
    return acc;
  }, {});

  // Section label helper
  const SectionHeader = ({ label }) => (
    <div style={{
      fontWeight: 600, fontSize: 11, color: '#6366f1',
      textTransform: 'uppercase', letterSpacing: 1,
      borderBottom: '1px solid #e0e7ff', paddingBottom: 4, marginBottom: 12,
    }}>{label}</div>
  );

  return (
    <div>
      <PageHeader title="Admission Management" icon="bi-person-plus-fill"
        actions={
          <div className="d-flex gap-2 flex-wrap">
            <select className="form-select form-select-sm" style={{ width: 200 }}
              value={filterStatus} onChange={e => { setFilterStatus(e.target.value); setPage(1); }}>
              <option value="">All Stages</option>
              {STEPS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <div className="input-group" style={{ width: 210 }}>
              <span className="input-group-text"><i className="bi bi-search"></i></span>
              <input className="form-control form-control-sm" placeholder="Search applicant…"
                value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
            </div>
            <button className="btn btn-primary btn-sm" onClick={openNew}>
              <i className="bi bi-plus-lg me-1"></i>New Application
            </button>
          </div>
        }
      />

      <ModuleHeaderStrip
        icon="bi-person-plus-fill"
        label="Admissions"
        chips={[
          { label: 'Total applications', value: total },
          { label: 'Filter stage',       value: filterStatus || 'All' },
        ]}
        variant="default"
      />

      {/* Pipeline stats */}
      <div className="row g-2 mb-3">
        {STEPS.map(s => (
          <div key={s} style={{ flex: '1 1 120px', maxWidth: 160 }}>
            <div className="card text-center h-100"
              style={{ borderTop: `3px solid ${STEP_COLORS[s]}`, borderRadius: 10, cursor: 'pointer' }}
              onClick={() => setFilterStatus(filterStatus === s ? '' : s)}>
              <div className="card-body py-2 px-1">
                <div style={{ fontSize: 22, fontWeight: 800, color: STEP_COLORS[s] }}>
                  {statsByStatus[s] || 0}
                </div>
                <div style={{ fontSize: 9, color: '#64748b', fontWeight: 600, lineHeight: 1.3 }}>{s}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* View Detail Modal */}
      {viewRecord && !showForm && (
        <div className="card mb-4" style={{ border: '2px solid #e0e7ff', borderRadius: 12 }}>
          <div className="card-header d-flex justify-content-between align-items-center"
            style={{ background: '#f5f3ff' }}>
            <span style={{ fontWeight: 700, fontSize: 14 }}>
              <i className="bi bi-person me-2 text-primary"></i>
              {viewRecord.recordData?.applicant_name}
            </span>
            <div className="d-flex gap-2">
              <button className="btn btn-outline-primary btn-sm" onClick={() => openEdit(viewRecord)}>
                <i className="bi bi-pencil me-1"></i>Edit
              </button>
              <button className="btn-close" onClick={() => setViewRecord(null)}></button>
            </div>
          </div>
          <div className="card-body">
            <div className="row g-3" style={{ fontSize: 13 }}>
              {[
                ['Programme', viewRecord.recordData?.programme],
                ['Class / Grade', viewRecord.recordData?.class_grade],
                ['Phone', viewRecord.recordData?.phone],
                ['Email', viewRecord.recordData?.email],
                ['D.O.B', viewRecord.recordData?.dob],
                ['Gender', viewRecord.recordData?.gender],
                ['Father', viewRecord.recordData?.father_name],
                ['Mother', viewRecord.recordData?.mother_name],
                ['Parent Phone', viewRecord.recordData?.parent_phone],
                ['Previous School', viewRecord.recordData?.has_previous_school ? viewRecord.recordData?.previous_school : 'Not Applicable'],
                ['Last % / Grade', viewRecord.recordData?.percentage],
                ['Source', viewRecord.recordData?.source],
                ['Test Date', viewRecord.recordData?.test_date ? new Date(viewRecord.recordData.test_date).toLocaleString() : '—'],
              ].map(([label, val]) => (
                <div className="col-md-4 col-6" key={label}>
                  <div style={{ color: '#94a3b8', fontSize: 11 }}>{label}</div>
                  <div style={{ fontWeight: 600 }}>{val || '—'}</div>
                </div>
              ))}
              {viewRecord.recordData?.remarks && (
                <div className="col-12">
                  <div style={{ color: '#94a3b8', fontSize: 11 }}>Remarks</div>
                  <div>{viewRecord.recordData.remarks}</div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Form */}
      {showForm && (
        <div className="card mb-4" style={{ borderRadius: 12, border: '2px solid #c7d2fe' }}>
          <div className="card-header d-flex justify-content-between align-items-center"
            style={{ background: '#f5f3ff' }}>
            <span style={{ fontWeight: 700, fontSize: 14 }}>
              <i className="bi bi-person-plus me-2 text-primary"></i>
              {editing ? `Edit — ${editing.recordData?.applicant_name}` : 'New Admission Application'}
            </span>
            <button className="btn-close" onClick={() => { setShowForm(false); setEditing(null); }}></button>
          </div>
          <div className="card-body">
            <form onSubmit={handleSave}>

              {/* Personal Info */}
              <SectionHeader label="Personal Information" />
              <div className="row g-3 mb-4">
                <div className="col-md-4">
                  <label className="form-label">Full Name <span className="text-danger">*</span></label>
                  <input className="form-control" required value={form.applicant_name} onChange={sfE('applicant_name')} />
                </div>
                <div className="col-md-2">
                  <label className="form-label">Date of Birth</label>
                  <input type="date" className="form-control" value={form.dob} onChange={sfE('dob')} />
                </div>
                <div className="col-md-2">
                  <label className="form-label">Gender</label>
                  <select className="form-select" value={form.gender} onChange={sfE('gender')}>
                    <option value="">Select</option>
                    <option>Male</option><option>Female</option><option>Other</option>
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="form-label">Email</label>
                  <input type="email" className="form-control" value={form.email} onChange={sfE('email')} />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Phone <span className="text-danger">*</span></label>
                  <input type="tel" className="form-control" required value={form.phone} onChange={sfE('phone')} />
                </div>
                <div className="col-md-5">
                  <label className="form-label">Address</label>
                  <input className="form-control" value={form.address} onChange={sfE('address')} />
                </div>
              </div>

              {/* Family */}
              <SectionHeader label="Family Details" />
              <div className="row g-3 mb-4">
                <div className="col-md-4">
                  <label className="form-label">Father's Name</label>
                  <input className="form-control" value={form.father_name} onChange={sfE('father_name')} />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Mother's Name</label>
                  <input className="form-control" value={form.mother_name} onChange={sfE('mother_name')} />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Parent / Guardian Phone</label>
                  <input type="tel" className="form-control" value={form.parent_phone} onChange={sfE('parent_phone')} />
                </div>
              </div>

              {/* Academic */}
              <SectionHeader label="Academic Details" />
              <div className="row g-3 mb-4">
                <div className="col-md-4">
                  <label className="form-label">Class / Programme applied for: <span className="text-danger">*</span></label>
                  <select className="form-select" required value={form.programme} onChange={sfE('programme')}>
                    <option value="">Select class / programme</option>
                    {classOptions.map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div className="col-md-2">
                  <label className="form-label">Section Preference</label>
                  <select className="form-select" value={form.section || ''} onChange={sfE('section')}>
                    <option value="">Any</option>
                    {sectionOptions.map(s => <option key={s}>{s}</option>)}
                  </select>
                </div>
                <div className="col-md-3">
                  <label className="form-label">Lead Source</label>
                  <select className="form-select" value={form.source} onChange={sfE('source')}>
                    <option value="">Select source</option>
                    {SOURCES.map(s => <option key={s}>{s}</option>)}
                  </select>
                  {form.source === 'Other' && (
                    <input className="form-control mt-1" placeholder="Specify source"
                      value={otherSource} onChange={e => setOtherSource(e.target.value)} autoFocus />
                  )}
                </div>

                {/* Previous School — optional */}
                <div className="col-md-3 d-flex align-items-end pb-1">
                  <div className="form-check form-switch">
                    <input className="form-check-input" type="checkbox" id="hasPrevSchool"
                      checked={!!form.has_previous_school}
                      onChange={e => sf('has_previous_school')(e.target.checked)} />
                    <label className="form-check-label" htmlFor="hasPrevSchool" style={{ fontSize: 13 }}>
                      Has Previous School
                    </label>
                  </div>
                </div>

                {form.has_previous_school && (
                  <>
                    <div className="col-md-5">
                      <label className="form-label">Previous School / College</label>
                      <input className="form-control" value={form.previous_school} onChange={sfE('previous_school')} />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Last Percentage / Grade</label>
                      <input className="form-control" value={form.percentage} onChange={sfE('percentage')} />
                    </div>
                  </>
                )}
              </div>

              {/* Status & Test/Interview */}
              <SectionHeader label="Status & Test / Interview" />
              <div className="row g-3 mb-3">
                <div className="col-md-3">
                  <label className="form-label">Application Status</label>
                  <select className="form-select" value={form.status} onChange={sfE('status')}>
                    {STEPS.map(s => <option key={s}>{s}</option>)}
                  </select>
                </div>
                <div className="col-md-3">
                  <label className="form-label">Test / Interview Date</label>
                  <input type="datetime-local" className="form-control"
                    value={form.test_date} onChange={sfE('test_date')} />
                </div>
                <div className="col-md-3 d-flex align-items-end pb-1">
                  <div className="form-check form-switch">
                    <input className="form-check-input" type="checkbox" id="docsub"
                      checked={!!form.documents_submitted}
                      onChange={e => sf('documents_submitted')(e.target.checked)} />
                    <label className="form-check-label" htmlFor="docsub" style={{ fontSize: 13 }}>
                      Documents Submitted
                    </label>
                  </div>
                </div>
                <div className="col-md-5">
                  <label className="form-label">Test / Interview Notes</label>
                  <textarea className="form-control" rows={2}
                    value={form.test_notes} onChange={sfE('test_notes')} />
                </div>
                <div className="col-md-7">
                  <label className="form-label">Remarks</label>
                  <textarea className="form-control" rows={2}
                    value={form.remarks} onChange={sfE('remarks')} />
                </div>
              </div>

              <div className="d-flex gap-2 justify-content-end pt-2 border-top">
                <button type="button" className="btn btn-secondary btn-sm"
                  onClick={() => { setShowForm(false); setEditing(null); }}>Cancel</button>
                <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>
                  {saving && <span className="spinner-border spinner-border-sm me-1"></span>}
                  {editing ? 'Update Application' : 'Submit Application'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="card">
        <div className="card-body p-0">
          {loading ? (
            <div className="text-center py-5"><span className="spinner-border text-primary"></span></div>
          ) : records.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <i className="bi bi-person-plus display-4 d-block mb-3 opacity-25"></i>
              No applications found.{' '}
              <button className="btn btn-link p-0" onClick={openNew}>Add first application</button>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover mb-0" style={{ fontSize: 13 }}>
                <thead style={{ background: '#f8fafc' }}>
                  <tr>
                    <th>Applicant</th>
                    <th>Programme / Class</th>
                    <th>Phone</th>
                    <th>Source</th>
                    <th>Docs</th>
                    <th>Test / Interview</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {records.map(r => {
                    const d = r.recordData || {};
                    return (
                      <tr key={r._id} style={{ cursor: 'pointer' }}
                        onClick={() => { setViewRecord(r); setShowForm(false); }}>
                        <td>
                          <div style={{ fontWeight: 600 }}>{d.applicant_name}</div>
                          <div style={{ fontSize: 11, color: '#94a3b8' }}>{d.email}</div>
                        </td>
                        <td>
                          <div>{d.programme || <span className="text-muted">—</span>}</div>
                          {d.section && <div style={{ fontSize: 11, color: '#94a3b8' }}>Sec. {d.section}</div>}
                        </td>
                        <td>{d.phone}</td>
                        <td style={{ color: '#64748b' }}>{d.source || '—'}</td>
                        <td>
                          {d.documents_submitted
                            ? <i className="bi bi-check-circle-fill text-success"></i>
                            : <i className="bi bi-x-circle text-muted"></i>}
                        </td>
                        <td style={{ color: '#64748b', fontSize: 12 }}>
                          {d.test_date ? new Date(d.test_date).toLocaleDateString() : '—'}
                        </td>
                        <td>
                          <span className="badge"
                            style={{
                              background: `${STEP_COLORS[d.status] || '#6c757d'}20`,
                              color: STEP_COLORS[d.status] || '#6c757d',
                              fontSize: 11,
                            }}>
                            {d.status || 'Applied'}
                          </span>
                        </td>
                        <td onClick={e => e.stopPropagation()}>
                          <div className="d-flex gap-1 flex-wrap">
                            {isAdmin && (
                              <div className="dropdown">
                                <button className="btn btn-outline-secondary btn-sm dropdown-toggle"
                                  style={{ fontSize: 11, padding: '2px 6px' }}
                                  data-bs-toggle="dropdown">Move</button>
                                <ul className="dropdown-menu">
                                  {STEPS.filter(s => s !== d.status).map(s => (
                                    <li key={s}>
                                      <button className="dropdown-item" style={{ fontSize: 12 }}
                                        onClick={() => moveStep(r, s)}>
                                        <span className="me-2" style={{ color: STEP_COLORS[s] }}>●</span>{s}
                                      </button>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            <button className="btn btn-outline-primary btn-sm"
                              style={{ padding: '2px 6px' }} onClick={() => openEdit(r)}>
                              <i className="bi bi-pencil"></i>
                            </button>
                            {isAdmin && (
                              <button className="btn btn-outline-danger btn-sm"
                                style={{ padding: '2px 6px' }} onClick={() => handleDelete(r._id)}>
                                <i className="bi bi-trash"></i>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <Pagination page={page} totalPages={Math.ceil(total / 20)} onPageChange={setPage} total={total} limit={20} />
      <AlertToast {...toast} onClose={() => setToast(t => ({ ...t, show: false }))} />
    </div>
  );
}
